'use client';

/**
 * Client-side auth hook.
 *
 * IMPORTANT — what `localStorage` is used for:
 *   - UI cache only: lets the navbar / dashboard show "Hi, Ravi" without
 *     a server round-trip on every page load.
 *
 * What it is NOT used for:
 *   - Authorization. Server APIs derive identity from the signed httpOnly
 *     session cookie set by /api/auth/login. localStorage is unprivileged.
 *
 * If a malicious user edits localStorage to claim role=admin, nothing
 * privileged opens up — server still refuses with 401/403.
 */

import { useState, useEffect, createContext, useContext } from 'react';
import { useRouter } from 'next/navigation';
import { setSessionTimeoutMinutes } from '@/lib/adminSession';

interface User {
    id: string;
    name: string;
    email: string;
    phone?: string;
    role: 'customer' | 'admin' | 'executioner';
    /** Admin only: allowed areas e.g. ['operations','cms']. Empty = full access. */
    allowedAccess?: string[];
    /** Admin only: true if this user is THE super admin (can create/delete
     *  other admins, edit permissions, etc.). Regular admins are false. */
    isSuperAdmin?: boolean;
    /** Whether this user has email 2FA enabled (admin login). */
    twoFactorEnabled?: boolean;
    /** Admin only: idle session timeout in minutes (0 = never). */
    sessionTimeoutMinutes?: number;
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (userData: User) => void;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const router = useRouter();

    useEffect(() => {
        // Two-phase init:
        //   1. Hydrate from localStorage immediately so the UI doesn't flash
        //      a logged-out state on every refresh.
        //   2. Then fetch /api/auth/me to get fresh server-side fields
        //      (allowedAccess, isSuperAdmin, role demotions, etc.) and
        //      overwrite the cache. This keeps the client's view of the user
        //      in sync without forcing an explicit logout/login after admin
        //      changes their permissions or role server-side.
        const savedUser = localStorage.getItem('magia_user');
        if (savedUser) {
            try {
                setUser(JSON.parse(savedUser));
            } catch (error) {
                localStorage.removeItem('magia_user');
            }
        }

        let cancelled = false;
        (async () => {
            try {
                const res = await fetch('/api/auth/me', { credentials: 'include' });
                if (!res.ok) {
                    // 401 = cookie expired / role mismatch → drop the cache.
                    // Don't bounce to /login here; let route guards handle that
                    // so the public site doesn't get unwanted redirects.
                    if (res.status === 401) {
                        localStorage.removeItem('magia_user');
                        if (!cancelled) setUser(null);
                    }
                    return;
                }
                const fresh = await res.json();
                if (cancelled || !fresh?.id) return;
                setUser(fresh);
                localStorage.setItem('magia_user', JSON.stringify(fresh));
                if (fresh.role === 'admin' && typeof fresh.sessionTimeoutMinutes === 'number') {
                    setSessionTimeoutMinutes(fresh.sessionTimeoutMinutes);
                }
            } catch {
                /* network blip — keep the cached user, retry next mount */
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();

        // If the fetch above is slow we still want isLoading to flip false
        // promptly so guarded pages don't sit on a spinner forever. The
        // localStorage value is enough to make routing decisions; the /me
        // refresh is best-effort.
        setIsLoading(false);

        return () => {
            cancelled = true;
        };
    }, []);

    const login = (userData: User) => {
        setUser(userData);
        localStorage.setItem('magia_user', JSON.stringify(userData));
        if (userData.role === 'admin' && typeof userData.sessionTimeoutMinutes === 'number') {
            setSessionTimeoutMinutes(userData.sessionTimeoutMinutes);
        }
    };

    const logout = async () => {
        // Tell the server to clear the httpOnly session cookie. Without this,
        // the cookie would persist and the user would still be authenticated
        // server-side even after we wipe localStorage.
        try {
            await fetch('/api/auth/logout', { method: 'POST' });
        } catch {
            // Even if the network call fails, still clear client state below.
        }
        setUser(null);
        localStorage.removeItem('magia_user');
        router.push('/');
    };

    return (
        <AuthContext.Provider data-component-id="src/hooks/useAuth.tsx:134:8" data-component-path="src/hooks/useAuth.tsx" data-component-line="134" data-component-end-line="136" data-component-file="useAuth.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%7D" value={{ user, isAuthenticated: !!user, isLoading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
